'use client';

import { useCallback, useEffect, useState } from 'react';

import { api, getErrorMessage } from '$lib/api/client';
import type {
	AttendanceBreakRecordRequest,
	AttendanceResponse,
	AttendanceWorkMode,
	CheckInRequest,
	LocationDto,
	UpdateTodayAttendanceRequest
} from '$lib/api/types';
import type { AttendanceActionKind } from '$lib/attendance/attendance-actions';

type AttendanceActionOptions = {
	workMode?: AttendanceWorkMode;
};

type AttendanceRequestState =
	| { status: 'idle' }
	| { status: 'loading' }
	| { status: 'success'; data: AttendanceResponse | null }
	| { status: 'error'; message: string };

const CHECKOUT_RETRY_DELAY_MS = 300;

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientCheckoutError(error: unknown): boolean {
	const message = getErrorMessage(error).toLowerCase();
	return (
		message.includes('failed to fetch') ||
		message.includes('network') ||
		message.includes('timeout') ||
		message.includes('timed out') ||
		message.includes('connection') ||
		message.includes('요청 실패 (5') ||
		message.includes('일시')
	);
}

function getBrowserLocation(): Promise<LocationDto | null> {
	if (typeof navigator === 'undefined' || !navigator.geolocation) {
		return Promise.resolve(null);
	}

	return new Promise((resolve) => {
		navigator.geolocation.getCurrentPosition(
			(position) =>
				resolve({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
					accuracyMeters: position.coords.accuracy
				}),
			() => resolve(null),
			{ enableHighAccuracy: true, timeout: 5000, maximumAge: 30000 }
		);
	});
}

export function useTodayAttendance({ autoLoad = true }: { autoLoad?: boolean } = {}) {
	const [state, setState] = useState<AttendanceRequestState>({ status: 'idle' });
	const [actionBusy, setActionBusy] = useState(false);
	const [actionError, setActionError] = useState<string | null>(null);

	const load = useCallback(async () => {
		setState({ status: 'loading' });
		try {
			const data = await api.get<AttendanceResponse | null>('/attendances/today');
			setState({ status: 'success', data });
		} catch (error) {
			setState({ status: 'error', message: getErrorMessage(error) });
		}
	}, []);

	const performAction = useCallback(async (kind: AttendanceActionKind, options: AttendanceActionOptions = {}) => {
		if (kind === 'complete') return null;

		setActionBusy(true);
		setActionError(null);
		try {
			let data: AttendanceResponse;
			switch (kind) {
				case 'check-in':
				case 'check-out': {
					const location = await getBrowserLocation();
					const body: CheckInRequest = {
						...(kind === 'check-in' && options.workMode ? { workMode: options.workMode } : {}),
						...(location ? { location } : {})
					};
					const path = kind === 'check-in' ? '/attendances/check-in' : '/attendances/check-out';
					try {
						data = await api.post<AttendanceResponse>(path, body);
					} catch (error) {
						if (kind !== 'check-out' || !isTransientCheckoutError(error)) {
							throw error;
						}
						await delay(CHECKOUT_RETRY_DELAY_MS);
						data = await api.post<AttendanceResponse>(path, body);
					}
					break;
				}
				case 'break-start':
					data = await api.post<AttendanceResponse>('/attendances/break/start', {});
					break;
				case 'break-end':
					data = await api.post<AttendanceResponse>('/attendances/break/end');
					break;
				default: {
					const _exhaustive: never = kind;
					return _exhaustive;
				}
			}
			setState({ status: 'success', data });
			return data;
		} catch (error) {
			const message = getErrorMessage(error);
			if (kind === 'check-out') {
				try {
					const current = await api.get<AttendanceResponse | null>('/attendances/today');
					if (current?.checkOutTime) {
						setState({ status: 'success', data: current });
						setActionError(null);
						return current;
					}
				} catch {
					// Preserve the original action error when the reconciliation read also fails.
				}
			}
			setActionError(message);
			return null;
		} finally {
			setActionBusy(false);
		}
	}, []);

	const updateToday = useCallback(async (request: UpdateTodayAttendanceRequest) => {
		setActionBusy(true);
		setActionError(null);
		try {
			const data = await api.patch<AttendanceResponse>('/attendances/today', request);
			setState({ status: 'success', data });
			return data;
		} catch (error) {
			setActionError(getErrorMessage(error));
			return null;
		} finally {
			setActionBusy(false);
		}
	}, []);

	const createBreakRecord = useCallback(async (request: AttendanceBreakRecordRequest) => {
		setActionBusy(true);
		setActionError(null);
		try {
			const data = await api.post<AttendanceResponse>('/attendances/breaks', request);
			setState({ status: 'success', data });
			return data;
		} catch (error) {
			setActionError(getErrorMessage(error));
			return null;
		} finally {
			setActionBusy(false);
		}
	}, []);

	const updateBreakRecord = useCallback(async (breakId: number, request: AttendanceBreakRecordRequest) => {
		setActionBusy(true);
		setActionError(null);
		try {
			const data = await api.patch<AttendanceResponse>(`/attendances/breaks/${breakId}`, request);
			setState({ status: 'success', data });
			return data;
		} catch (error) {
			setActionError(getErrorMessage(error));
			return null;
		} finally {
			setActionBusy(false);
		}
	}, []);

	const deleteBreakRecord = useCallback(async (breakId: number) => {
		setActionBusy(true);
		setActionError(null);
		try {
			const data = await api.delete<AttendanceResponse>(`/attendances/breaks/${breakId}`);
			setState({ status: 'success', data });
			return data;
		} catch (error) {
			setActionError(getErrorMessage(error));
			return null;
		} finally {
			setActionBusy(false);
		}
	}, []);

	useEffect(() => {
		if (!autoLoad) return;
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [autoLoad, load]);

	return {
		state,
		actionBusy,
		actionError,
		load,
		performAction,
		updateToday,
		createBreakRecord,
		updateBreakRecord,
		deleteBreakRecord
	};
}
